export type StoredAccess = {
    is_admin?: boolean;
    role?: string | null;
    can_manage_access?: boolean;
    enabled_modules?: string[];
    scopes?: {
        orgs?: string[];
        org_names?: string[];
        deposits?: string[];
        deposit_names?: string[];
        dns?: string[];
        dns_names?: string[];
    };
};

export const FALLBACK_ROLE_MODULES: Record<string, string[]> = {
    CONTRACTOR: ["repairs", "equipages", "bsi", "graph", "cliff", "ppk", "control_requests", "valves"],
    MASTER_DNS: ["graph", "cliff", "control_requests", "valves"],
    OSF: ["graph", "cliff", "control_requests", "valves"],
    CLUSTER: ["graph", "cliff", "control_requests", "valves"],
};

export function getStoredAccess(): StoredAccess {
    try {
        return JSON.parse(localStorage.getItem('access') || '{}');
    } catch {
        return {};
    }
}

export function isBusinessAdmin(access: StoredAccess = getStoredAccess()) {
    if (access?.role === 'CONTRACTOR') {
        return false;
    }
    return Boolean(access?.is_admin) || access?.role === 'ADMIN';
}

export function canManageAccess(access: StoredAccess = getStoredAccess()) {
    if (access?.role === 'CONTRACTOR') {
        return false;
    }
    return isBusinessAdmin(access) || Boolean(access?.can_manage_access);
}

export function canManageValveIssues(access: StoredAccess = getStoredAccess()) {
    if (access?.role === 'CONTRACTOR') {
        return false;
    }
    return ['ADMIN', 'MASTER_DNS', 'OSF', 'CLUSTER'].includes(String(access?.role || ''))
        || Boolean(access?.is_admin);
}

export function getEnabledModules(access: StoredAccess = getStoredAccess()) {
    return Array.isArray(access?.enabled_modules) ? access.enabled_modules : [];
}

export function canUseModule(moduleId: string, access: StoredAccess = getStoredAccess()) {
    if (isBusinessAdmin(access)) {
        return true;
    }

    const role = access?.role || 'CONTRACTOR';
    const roleModules = FALLBACK_ROLE_MODULES[role] || FALLBACK_ROLE_MODULES.CONTRACTOR;
    const enabledModules = getEnabledModules(access);
    if (enabledModules.length) {
        return roleModules.includes(moduleId) && enabledModules.includes(moduleId);
    }

    return roleModules.includes(moduleId);
}

export function getScopedOrgNames(access: StoredAccess = getStoredAccess()) {
    const orgNames = access.scopes?.org_names;
    return Array.isArray(orgNames) ? orgNames : [];
}

export function getPrimaryOrgName(access: StoredAccess = getStoredAccess()) {
    const orgNames = getScopedOrgNames(access);
    return orgNames.length === 1 ? orgNames[0] : '';
}

export function hasSingleOrgScope(access: StoredAccess = getStoredAccess()) {
    return getScopedOrgNames(access).length === 1;
}

export function getStoredUsername() {
    return localStorage.getItem('username') || '';
}
